// src/admin/features/clients/viewClientDetails/accountInfoClientTab/index.tsx
import { TeamSelector } from 'app/features/profile/businessDetails/previewBusinessDetails';

import { AddIcon } from 'assets/icons';
import {
  IUser,
  useAssignIndividualTeamClientMutation,
  useDeleteIndividualTeamClientByIdMutation,
  useGetCognitoUserAccountStatusQuery,
  useGetUserProfileByIdQuery,
  useResendInvitationMutation,
  useUpdateClientActiveStatusMutation,
} from 'app/services/user';
import {
  AddressIcon,
  CheckedIcon,
  CityIcon,
  CountryIcon,
  EditIcon,
  EmailIcon,
  IndustryIcon,
  LanguageIcon,
  PasswordProfileIcon,
  PhoneIcon,
  ShareIcon,
  StateIcon,
  TimeZoneIcon,
  ZipcodeIcon,
} from 'assets/icons';
import Button from 'Components/Buttons/Button';
import { successToast, errorToast } from 'Components/Notifications';
import UserInfo from 'Components/Profile/UserInfo';

import Spinner from 'Components/Spinner';

import Timezone from 'Components/Timezone';
import Tooltip from 'Components/Tooltips';
import UserAvatar from 'Components/UserAvatar';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from 'utils/AuthProvider';
type FormInputs = {
  given_name: string;
  family_name: string;
  email: string;
  zipcode: string;
  country: string;
  state: string;
  city: string;
  address: string;
  service_type: string;
  specialization_id?: number;
  language_id?: number;
  time_zone_id?: number;
  is_verified_phone_number: boolean;
};

export function useGetClientProfile() {
  const { clientId: _clientId } = useParams();

  const { isClient, userId, user } = useAuth();
  const clientId = isClient ? userId : +_clientId!;
  const { data } = useGetUserProfileByIdQuery(+clientId!, {
    skip: isClient || !clientId,
  });

  const client = isClient ? user : data?.user;

  return { client, clientId, isClient };
}

function useAccountVerficationStatus() {
  const { clientId, isClient } = useGetClientProfile();
  const { data: verificationStatus } = useGetCognitoUserAccountStatusQuery(
    clientId!,
    { skip: isClient },
  );
  const [ReinviteTrigger, { isLoading: isReinviting }] =
    useResendInvitationMutation();
  const ResendInvitation = () => {
    ReinviteTrigger(clientId!)
      .unwrap()
      .then(() => successToast('Invitation Sent'));
  };

  return {
    verificationStatus,
    ResendInvitation,
    isReinviting,
  };
}

function VerificationStatusButton() {
  const { ResendInvitation, isReinviting, verificationStatus } =
    useAccountVerficationStatus();
  if (!verificationStatus)
    return (
      <Button>
        <Spinner bounce={false} />
      </Button>
    );
  return (
    <>
      {verificationStatus.user_status === 'CONFIRMED' && (
        <div className="text-green h-8 flex gap-2 items-center font-semibold text-xs rounded p-1.5 shadow  uppercase ">
          verified
          <CheckedIcon className="w-4 h-4" />
        </div>
      )}

      {verificationStatus.user_status === 'FORCE_CHANGE_PASSWORD' && (
        <Tooltip message="Resend Invitation">
          <Button
            isLoading={isReinviting}
            theme="gray-fill"
            className="uppercase text-xs"
            onClick={ResendInvitation}
          >
            Pending
            {!isReinviting && <ShareIcon className="w-4 h-4" />}
          </Button>
        </Tooltip>
      )}
    </>
  );
}

function ToggleActiveStateButton() {
  const { client } = useGetClientProfile();
  const [UpdateStatus, { isLoading: isUpdatingStatus }] =
    useUpdateClientActiveStatusMutation();

  const ToggleAccountState = () =>
    client &&
    UpdateStatus({ id: client.id, activate: !client.active_member })
      .unwrap()
      .then((response) =>
        successToast(
          `Marked client as ${
            response.user.active_member ? 'active' : 'inactive'
          }`,
        ),
      );
  if (!client) return null;

  return (
    <Button
      className="text-xs uppercase"
      isLoading={isUpdatingStatus}
      onClick={ToggleAccountState}
      theme={client.active_member ? 'red-outline' : 'green-fill'}
    >
      {client.active_member ? 'Mark as Inactive' : 'Mark as Active'}
    </Button>
  );
}

function ClientIndividualServicesDefaultTeam() {
  const { client, clientId } = useGetClientProfile();
  const [teamMembers, setTeamMembers] = useState(client?.team_members);

  const [DeleteIndividualTeam] = useDeleteIndividualTeamClientByIdMutation();
  const [AssignIndividualTeam] = useAssignIndividualTeamClientMutation();

  if (!client) return null;

  const addTeamMember = async (allUsers: IUser[]) => {
    const res = await AssignIndividualTeam({
      team: {
        client_id: clientId!,
        assignee_ids: allUsers.map((t) => t.id),
      },
    }).unwrap();
    setTeamMembers(res.team_members);
  };

  const DeleteIndividualTeamMember = (member: IUser) =>
    DeleteIndividualTeam([clientId!, member.id]).then(() =>
      setTeamMembers(teamMembers?.filter((t) => t.id !== member.id)),
    );

  return (
    <TeamSelector
      title="Individual Services Default Team"
      team={teamMembers || []}
      onDeleteMember={DeleteIndividualTeamMember}
      onConfirm={addTeamMember}
    />
  );
}

function ClientDataPreview() {
  const { client } = useGetClientProfile();
  if (!client) return null;
  return (
    <div className="grid text-xs gap-4 pt-4">
      <UserInfo
        className="col-span-2"
        userInfo={client.email || 'N/A'}
        label="Email Address"
        icon={<EmailIcon />}
      />

      <UserInfo
        userInfo={client.phone_number || 'N/A'}
        label="Phone Number"
        icon={<PhoneIcon />}
      />

      <UserInfo
        userInfo={client.specialization?.name || 'N/A'}
        label="Industry"
        icon={<IndustryIcon className="text-green" />}
      />

      <UserInfo
        userInfo={client.country || 'N/A'}
        label="Country"
        icon={<CountryIcon />}
      />

      <UserInfo
        userInfo={client.language?.name || 'N/A'}
        label="Language"
        icon={<LanguageIcon className="text-green" />}
      />

      <UserInfo
        className="col-span-2"
        userInfo={client.address || 'N/A'}
        label="Street Address"
        icon={<AddressIcon className="text-green" />}
      />

      <UserInfo
        userInfo={client.state || 'N/A'}
        label="State"
        icon={<StateIcon />}
      />

      <UserInfo
        userInfo={client.city || 'N/A'}
        label="City"
        icon={<CityIcon />}
      />
      <UserInfo
        userInfo={client.zipcode || 'N/A'}
        label="Zipcode"
        icon={<ZipcodeIcon />}
      />

      <UserInfo
        userInfo={<Timezone value={client.time_zone} fallback="N/A" />}
        label="Timezone"
        icon={<TimeZoneIcon className="text-green" />}
      />
    </div>
  );
}
export default function AccountInfoClientTab() {
  const navigate = useNavigate();
  const { isClient, isAdmin } = useAuth();
  const { client } = useGetClientProfile();
  const DRAFT_PASSWORD = process.env.REACT_APP_PASSWORD;
  if (!client) return <Spinner />;

  return (
    <div className="flex-1 w-full grid grid-cols-[2fr,1fr] overflow-y-auto md-max:overflow-y-auto justify-between md-max:grid-cols-1 p-4 gap-4">
      <div className="md-max:pb-4 md-max:border-b border-gray-100 ">
        <div className="flex flex-wrap gap-3">
          <UserAvatar user={client} />
          <div>
            <div className="font-semibold text-gray-dark capitalize flex flex-wrap items-center gap-2 min-w-0">
              <div> {`${client.given_name} ${client.family_name}`}</div>
              {isAdmin && (
                <>
                  <VerificationStatusButton />
                  <ToggleActiveStateButton />
                </>
              )}
              {isAdmin && (
                <Button
                  theme="green-fill"
                  className="text-xs uppercase whitespace-normal w-full md:w-auto"
                  onClick={() => {
                    const entered = window.prompt(
                      'Enter password to draft questionnaire:',
                    );
                    if (entered === DRAFT_PASSWORD) {
                      navigate('/dashboard/questionnaires/create', {
                        state: { clientId: client.id },
                      });
                    } else if (entered !== null) {
                      errorToast('Incorrect password');
                    }
                  }}
                >
                  <AddIcon className="w-4 h-4 mr-1" />
                  <span className="block md:inline">Draft</span>
                  <span className="block md:inline">Questionnaire</span>
                </Button>
              )}
              {isAdmin && (
                <Button
                  theme="green-fill"
                  className="text-xs uppercase whitespace-normal w-full md:w-auto"
                  onClick={() => {
                    const entered = window.prompt(
                      'Enter password to draft service:',
                    );
                    if (entered === DRAFT_PASSWORD) {
                      navigate(`/dashboard/clients/${client.id}/draft-service`);
                    } else if (entered !== null) {
                      errorToast('Incorrect password');
                    }
                  }}
                >
                  <AddIcon className="w-4 h-4 mr-1" />
                  <span className="block md:inline">Draft</span>
                  <span className="block md:inline">Service</span>
                </Button>
              )}
            </div>
            <div className="text-gray-700 text-xs">Client Account</div>
          </div>
        </div>
        <ClientDataPreview />

        {(isClient || isAdmin) && (
          <div className="w-1/2 flex flex-inline gap-4 mt-6 md-max:w-full md-max:grid">
            <Button type="button" onClick={() => navigate(`edit`)}>
              <EditIcon />
              Edit Profile
            </Button>
            {isClient && (
              <Button type="button" onClick={() => navigate(`changePassword`)}>
                <PasswordProfileIcon />
                Change Password
              </Button>
            )}
          </div>
        )}
      </div>

      <ClientIndividualServicesDefaultTeam />
    </div>
  );
}
